import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, School, FileText, MapPin, Phone, Mail, User, Download } from "lucide-react";
import api, { API } from "../lib/api";
import { formatFCFA, formatDate } from "../lib/format";
import { Spinner, StatusBadge } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PLAN_LABELS = { starter: "Starter", standard: "Standard", premium: "Premium" };
const ETYPE = { primaire: "Primaire", college: "College", lycee: "Lycee" };

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);

  useEffect(() => { api.get(`/customers/${id}`).then((r) => setC(r.data)).catch(() => setC(false)); }, [id]);

  if (c === null) return <Spinner />;
  if (c === false) return <p className="text-slate-500">Client introuvable.</p>;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-600">
        <ArrowLeft className="h-4 w-4" /> Retour
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-teal-400">
            <Building2 className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900">{c.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500 mt-1">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.city}</span>
              <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>
              {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
              {c.contact_person && <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{c.contact_person}</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:items-end gap-2 self-start">
          <span className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-2 text-sm font-semibold text-teal-700">Formule {PLAN_LABELS[c.plan]}</span>
          <Button data-testid="statement-pdf-btn" variant="outline" size="sm" onClick={() => window.open(`${API}/customers/${c.id}/statement/pdf`, "_blank")}>
            <Download className="h-4 w-4 mr-1.5" /> Releve de compte PDF
          </Button>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><School className="h-5 w-5 text-teal-500" /> Etablissements ({c.establishments?.length || 0})</h3>
          <div className="space-y-2">
            {(c.establishments || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-2.5">
                <div>
                  <p className="font-medium text-slate-800">{e.name}</p>
                  <p className="text-xs text-slate-400">{ETYPE[e.type] || e.type} · Dir. {e.director_name || "-"}</p>
                </div>
                <span className="font-mono text-sm text-slate-600">{e.student_count} eleves</span>
              </div>
            ))}
            {(!c.establishments || c.establishments.length === 0) && <p className="text-sm text-slate-400">Aucun etablissement.</p>}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Abonnements</h3>
          <div className="space-y-2">
            {(c.subscriptions || []).map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-2.5">
                <div>
                  <p className="font-medium text-slate-800">{s.plan_name} — {s.year}</p>
                  <p className="text-xs text-slate-400">{formatDate(s.start_date)} au {formatDate(s.end_date)}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-semibold">{formatFCFA(s.annual_amount)}</p>
                  <StatusBadge status={s.status} />
                </div>
              </div>
            ))}
            {(!c.subscriptions || c.subscriptions.length === 0) && <p className="text-sm text-slate-400">Aucun abonnement.</p>}
          </div>
        </Card>
      </div>

      <Card className="p-6 mt-8">
        <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><FileText className="h-5 w-5 text-teal-500" /> Factures</h3>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="px-6 py-3">N° Facture</TableHead>
              <TableHead>Exercice</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Solde</TableHead>
              <TableHead>Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(c.invoices || []).map((i) => (
              <TableRow key={i.id} className="cursor-pointer" onClick={() => navigate(`/factures?open=${i.id}`)}>
                <TableCell className="px-6 font-mono text-sm">{i.number}</TableCell>
                <TableCell>{i.year}</TableCell>
                <TableCell className="text-right font-mono">{formatFCFA(i.total_amount)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatFCFA(i.balance)}</TableCell>
                <TableCell><StatusBadge status={i.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {(!c.invoices || c.invoices.length === 0) && <p className="text-sm text-slate-400 py-4">Aucune facture.</p>}
      </Card>
    </div>
  );
}
